import { css } from '@emotion/react'
import { Alert, Heading, isEmpty, Text, Theme, useTheme } from 'bold-ui'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { Fragment, ReactNode, useMemo } from 'react'
import { humanizeAge } from 'util/date/humanize-age'
import { formatNomeCidadao } from 'view/cidadao/utils-cidadao'

import { MChatFooterInfo } from '../mchat/components/MChatFooterInfo'
import { MChatAnteriorFooterInfoModel, MChatRiscoPrevioRelevanteInfoModel } from '../mchat/model-mchat'
import { MCHAT_RF_RESULTADO_BY_STATUS, MChatRfResultadoStatus } from '../model-mChatRf'
import { MChatRAnteriorAlert } from './MChatRfAlert'
import { MChatRfResultadoAlert } from './MChatRfResultadoAlert'

interface MChatRfResultadoViewProps {
  status: MChatRfResultadoStatus | 'default'
  title: string
  label: string
  description: ReactNode
  content?: ReactNode
  footerInfo?: MChatAnteriorFooterInfoModel
  alertaMChatAnterior?: MChatRiscoPrevioRelevanteInfoModel
  alertaConsultaSeguimentoAnterior?: MChatRiscoPrevioRelevanteInfoModel
  deveExibirAlertaRiscoBaixoApos24Meses?: boolean
}

export function MChatRfResultadoView(props: Readonly<MChatRfResultadoViewProps>) {
  const {
    title,
    status,
    label,
    description,
    content,
    footerInfo,
    alertaMChatAnterior,
    alertaConsultaSeguimentoAnterior,
    deveExibirAlertaRiscoBaixoApos24Meses,
  } = props

  const theme = useTheme()

  const hasContent = !isEmpty(content)
  const hasFooterInfo = !isEmpty(footerInfo)
  const styles = useMemo(() => createStyles(theme, status), [theme, status])

  const {
    atendimentoProfissional: { iniciadoEm: dataAtendimento } = {},
    cidadao: { dataNascimento, nome, nomeSocial },
  } = useAtendimentoContext()

  const showDescription = description && !deveExibirAlertaRiscoBaixoApos24Meses && status !== 'default'
  const showAlert = deveExibirAlertaRiscoBaixoApos24Meses && dataNascimento && dataAtendimento
  const showFooterInfo = hasFooterInfo && dataNascimento && footerInfo

  return (
    <Fragment>
      <Heading level={3} fontWeight='normal' style={styles.heading}>
        <strong css={styles.title}>{title}</strong> | <Text fontSize={1}>{label}</Text>
      </Heading>
      {hasContent && <div css={styles.contentWrapper}>{content}</div>}
      {showDescription && (
        <MChatRfResultadoAlert status={status} style={styles.alert}>
          {description}
        </MChatRfResultadoAlert>
      )}
      {showAlert && (
        <div css={styles.alert}>
          <Alert type='warning'>
            <strong>Reavaliação recomendada:</strong> Foi calculado <strong>Risco baixo</strong> anteriormente e é
            recomendado reavaliar após o aniversário de dois anos da criança. {formatNomeCidadao({ nome, nomeSocial })}{' '}
            está com {humanizeAge(dataNascimento, dataAtendimento.getTime())}.
          </Alert>
        </div>
      )}
      {showFooterInfo && <MChatFooterInfo info={footerInfo} cidadaoDataNascimento={dataNascimento} />}
      {alertaMChatAnterior && <MChatRAnteriorAlert alerta={alertaMChatAnterior} />}
      {alertaConsultaSeguimentoAnterior && <MChatRAnteriorAlert alerta={alertaConsultaSeguimentoAnterior} />}
    </Fragment>
  )
}

const createStyles = (theme: Theme, status: MChatRfResultadoStatus | 'default') => {
  const titleColor =
    status === 'default' ? theme.pallete.text.main : MCHAT_RF_RESULTADO_BY_STATUS[status].colorScale.c40

  return {
    heading: css`
      margin-bottom: 0.5rem;
    `,
    title: css`
      color: ${titleColor};
    `,
    contentWrapper: css`
      padding: 0.5rem 0;
      margin-bottom: 0.5rem;
    `,
    alert: css`
      margin-bottom: 0.5rem;
    `,
  }
}
